import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Shield, ExternalLink, Share2, Chrome, Search,
  Loader2, Lock, Unlock, Bot, FileText,
  Filter, BarChart3, Globe, Newspaper, ShoppingCart, Code, MessageSquare,
  TrendingUp, TrendingDown, ArrowRight, Plus, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const CATEGORIES = [
  { key: 'all', label: 'All Sites', icon: Globe },
  { key: 'news', label: 'News', icon: Newspaper },
  { key: 'social', label: 'Social', icon: MessageSquare },
  { key: 'commerce', label: 'Commerce', icon: ShoppingCart },
  { key: 'dev', label: 'Dev / Knowledge', icon: Code },
];

const POSTURE_CONFIG = {
  restrictive: { label: 'Restrictive', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', Icon: Lock },
  selective:   { label: 'Selective',   color: '#D97706', bg: '#FFFBEB', border: '#FDE68A', Icon: Filter },
  open:        { label: 'Open',        color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0', Icon: Unlock },
  silent:      { label: 'Silent',      color: '#64748B', bg: '#F8FAFC', border: '#E2E8F0', Icon: Bot },
};

function PostureBadge({ posture }) {
  const cfg = POSTURE_CONFIG[posture] || POSTURE_CONFIG.silent;
  const { Icon } = cfg;
  return (
    <span
      data-testid={`posture-badge-${posture}`}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ color: cfg.color, backgroundColor: cfg.bg, border: `1px solid ${cfg.border}` }}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function FilePill({ file, found }) {
  const label = file.split('/').pop();
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
      found ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-50 text-slate-400 border border-slate-200'
    }`}>
      <FileText className="w-2.5 h-2.5" />
      {label}
    </span>
  );
}

const CAP_FILES = ['/llms.txt', '/.well-known/agents.json', '/.well-known/ai-plugin.json'];

function LeaderboardRow({ entry, rank }) {
  const posture = entry.posture;
  const bp = entry.ai_bot_posture;
  const shareUrl = entry.sharecard_url
    ? `${process.env.REACT_APP_BACKEND_URL}${entry.sharecard_url}`
    : null;

  return (
    <tr data-testid={`leaderboard-row-${entry.domain}`} className="group border-b border-slate-100 hover:bg-slate-50/80 transition-colors">
      <td className="px-4 py-3.5 text-center">
        <span className={`text-sm font-mono font-bold ${rank <= 3 ? 'text-[#007AFF]' : 'text-slate-400'}`}>{rank}</span>
      </td>
      <td className="px-4 py-3.5">
        <Link to={entry.report_url} className="font-semibold text-slate-900 hover:text-[#007AFF] transition-colors" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {entry.domain}
        </Link>
        <span className="block text-xs text-slate-400 mt-0.5 capitalize">{entry.category}</span>
      </td>
      <td className="px-4 py-3.5"><PostureBadge posture={posture} /></td>
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-4">
          <div className="text-center">
            <span className="text-lg font-bold font-mono text-red-600">{bp.blocked_count}</span>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Blocked</p>
          </div>
          <div className="text-center">
            <span className="text-lg font-bold font-mono text-emerald-600">{bp.allowed_count}</span>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Allowed</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3.5">
        <div className="flex flex-wrap gap-1">
          {CAP_FILES.map((f) => (
            <FilePill key={f} file={f} found={entry.files_found.includes(f)} />
          ))}
        </div>
      </td>
      <td className="px-4 py-3.5 text-right">
        <div className="flex items-center justify-end gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
          <Link to={entry.report_url}>
            <Button variant="outline" size="sm" className="h-8 text-xs rounded-full gap-1" data-testid={`view-report-${entry.domain}`}>
              <ExternalLink className="w-3 h-3" /> Report
            </Button>
          </Link>
          {shareUrl && (
            <Button variant="outline" size="sm" className="h-8 text-xs rounded-full gap-1" data-testid={`share-card-${entry.domain}`}
              onClick={() => window.open(shareUrl, '_blank')}>
              <Share2 className="w-3 h-3" /> Card
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function Section({ title, subtitle, icon: Icon, iconColor, entries, testId }) {
  if (!entries.length) return null;
  return (
    <section>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4" style={{ color: iconColor }} />
        <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h2>
        <span className="text-xs text-slate-400 ml-1">{subtitle}</span>
      </div>
      <div data-testid={testId} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-[11px] text-slate-500 uppercase tracking-wider font-medium">
              <th className="px-4 py-3 text-center w-12">#</th>
              <th className="px-4 py-3 text-left">Domain</th>
              <th className="px-4 py-3 text-left">Posture</th>
              <th className="px-4 py-3 text-left">Bots</th>
              <th className="px-4 py-3 text-left">Files</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, i) => (
              <LeaderboardRow key={entry.domain} entry={entry} rank={i + 1} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function LeaderboardPage() {
  const [data, setData] = useState(null);
  const [deltas, setDeltas] = useState(null);
  const [communitySubmissions, setCommunitySubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [scanUrl, setScanUrl] = useState('');
  const [scanLoading, setScanLoading] = useState(false);
  const [submitDomain, setSubmitDomain] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.getLeaderboard().then(setData),
      api.getLeaderboardDeltas().then(setDeltas).catch(() => {}),
      api.getCommunitySubmissions(10).then(d => setCommunitySubmissions(d.submissions || [])).catch(() => {}),
    ]).catch(console.error).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!data?.entries) return [];
    let entries = [...data.entries];
    if (activeCategory !== 'all') {
      entries = entries.filter((e) => e.category === activeCategory);
    }
    return entries;
  }, [data, activeCategory]);

  const mostRestrictive = useMemo(
    () => [...filtered].sort((a, b) => b.ai_bot_posture.blocked_count - a.ai_bot_posture.blocked_count).filter((e) => e.ai_bot_posture.blocked_count > 0).slice(0, 10),
    [filtered]
  );

  const mostOpen = useMemo(
    () => [...filtered].sort((a, b) => b.ai_bot_posture.allowed_count - a.ai_bot_posture.allowed_count || a.ai_bot_posture.blocked_count - b.ai_bot_posture.blocked_count).filter((e) => e.ai_bot_posture.allowed_count > 0).slice(0, 10),
    [filtered]
  );

  const handleScan = async (e) => {
    e.preventDefault();
    if (!scanUrl.trim()) return;
    setScanLoading(true);
    try {
      const result = await api.createScan(scanUrl.trim());
      navigate(result.status === 'done' && result.public_report_id ? `/r/${result.public_report_id}` : `/scan/${result.scan_id}`);
    } catch { toast.error('Failed to start scan'); }
    finally { setScanLoading(false); }
  };

  const lastUpdated = data?.last_updated
    ? new Date(data.last_updated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const botSetSize = data?.bot_set?.size || 14;

  // Pick a representative sharecard for og:image
  const ogImage = mostRestrictive[0]?.sharecard_url
    ? `${process.env.REACT_APP_BACKEND_URL}${mostRestrictive[0].sharecard_url}`
    : null;

  return (
    <div className="min-h-screen bg-white" data-testid="leaderboard-page">
      <Helmet>
        <title>AI Bot Leaderboard - AgentCheck</title>
        <meta name="description" content={`Declared AI bot posture for top websites based on robots.txt (${botSetSize}-bot set). See which sites block or allow GPTBot, ClaudeBot, and more.`} />
        <meta property="og:title" content="AI Bot Leaderboard - AgentCheck" />
        <meta property="og:description" content={`How top sites handle AI bots. Declared in robots.txt. Based on our ${botSetSize}-bot set.`} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${process.env.REACT_APP_BACKEND_URL}/leaderboard/ai-bots`} />
      </Helmet>

      {/* Header */}
      <header className="border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#007AFF] flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>AgentCheck</span>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Site Scan</Link>
              <Link to="/monitor" className="text-sm text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-1"><Chrome className="w-4 h-4" />Monitor</Link>
              <Link to="/pricing" className="text-sm text-[#007AFF] hover:text-[#0062CC] font-medium transition-colors">Pro</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero CTA Strip */}
      <section className="bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-50 border border-purple-100">
                  <BarChart3 className="w-3.5 h-3.5 text-purple-600" />
                  <span className="text-xs font-semibold text-purple-700">AI Bot Posture</span>
                </div>
                {lastUpdated && <span className="text-xs text-slate-400">Last updated: {lastUpdated}</span>}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="leaderboard-title">
                AI Bot Leaderboard
              </h1>
              <p className="text-sm text-slate-500 mt-1 max-w-xl">
                Declared in <span className="font-mono text-slate-700">robots.txt</span> &middot;
                Based on {botSetSize}-bot set &middot;
                {lastUpdated && <> Updated {lastUpdated}</>}
              </p>
            </div>
            <form onSubmit={handleScan} className="flex items-center gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="scan-url-input" className="h-11 pl-9 pr-4 rounded-full border-slate-200 text-sm" placeholder="Scan any domain..." value={scanUrl} onChange={(e) => setScanUrl(e.target.value)} />
              </div>
              <Button type="submit" disabled={scanLoading} data-testid="scan-submit-btn" className="h-11 px-6 rounded-full bg-[#007AFF] hover:bg-[#0062CC] text-white font-medium text-sm">
                {scanLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Scan'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Category Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {CATEGORIES.map(({ key, label, icon: Icon }) => (
            <button key={key} data-testid={`filter-${key}`} onClick={() => setActiveCategory(key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeCategory === key ? 'bg-slate-900 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}>
              <Icon className="w-3.5 h-3.5" />{label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 text-[#007AFF] animate-spin" /></div>
        ) : (
          <div className="space-y-10">
            <Section title="Most Restrictive" subtitle="Highest blocked count" icon={Lock} iconColor="#DC2626" entries={mostRestrictive} testId="section-restrictive" />
            <Section title="Most Open / Selective" subtitle="Explicitly allowing AI bots" icon={Unlock} iconColor="#16A34A" entries={mostOpen} testId="section-open" />

            {/* Weekly Deltas */}
            {deltas?.deltas?.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-4 h-4 text-amber-500" />
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>This Week's Changes</h2>
                  <span className="text-xs text-slate-400 ml-1">{deltas.deltas.length} domains changed</span>
                </div>
                <div data-testid="section-deltas" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {deltas.deltas.slice(0, 6).map((d) => (
                    <div key={d.domain} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-sm text-slate-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{d.domain}</span>
                        {d.posture_changed && (
                          <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full font-medium">Posture shift</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        {d.blocked_delta !== 0 && (
                          <span className={`flex items-center gap-0.5 font-medium ${d.blocked_delta > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                            {d.blocked_delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {d.blocked_delta > 0 ? '+' : ''}{d.blocked_delta} blocked
                          </span>
                        )}
                        {d.allowed_delta !== 0 && (
                          <span className={`flex items-center gap-0.5 font-medium ${d.allowed_delta > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {d.allowed_delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {d.allowed_delta > 0 ? '+' : ''}{d.allowed_delta} allowed
                          </span>
                        )}
                      </div>
                      {d.posture_changed && (
                        <div className="flex items-center gap-1.5 mt-2 text-[11px] text-slate-500">
                          <span className="capitalize">{d.posture_prev}</span>
                          <ArrowRight className="w-3 h-3" />
                          <span className="capitalize font-medium text-slate-700">{d.posture_now}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
            <Section title="All Scanned Sites" subtitle={`${filtered.length} domains`} icon={Globe} iconColor="#007AFF" entries={filtered} testId="section-all" />
            {filtered.length === 0 && !loading && (
              <div className="py-16 text-center text-slate-400 text-sm">No data available for this category.</div>
            )}
          </div>
        )}
      </div>

      {/* Community Submit + Recent */}
      <section className="border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Submit form */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Plus className="w-4 h-4 text-[#007AFF]" />
                <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Submit a Domain</h3>
              </div>
              <p className="text-xs text-slate-500 mb-4">Request any domain to be scanned. We'll generate an AI bot report and add it to the leaderboard.</p>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!submitDomain.trim() || submitLoading) return;
                setSubmitLoading(true);
                try {
                  const res = await api.submitDomain(submitDomain.trim());
                  toast.success(res.message || `${submitDomain} submitted!`);
                  setSubmitDomain('');
                  // Refresh community list after a delay
                  setTimeout(() => {
                    api.getCommunitySubmissions(10).then(d => setCommunitySubmissions(d.submissions || [])).catch(() => {});
                  }, 15000);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Submission failed');
                } finally {
                  setSubmitLoading(false);
                }
              }} className="flex gap-2">
                <Input
                  data-testid="submit-domain-input"
                  value={submitDomain}
                  onChange={(e) => setSubmitDomain(e.target.value)}
                  placeholder="example.com"
                  className="h-10 rounded-full flex-1 text-sm"
                />
                <Button type="submit" disabled={submitLoading || !submitDomain.trim()}
                  data-testid="submit-domain-btn"
                  className="h-10 px-5 rounded-full bg-[#007AFF] hover:bg-[#0062CC] text-white text-sm font-medium">
                  {submitLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit'}
                </Button>
              </form>
              <p className="text-[10px] text-slate-400 mt-2">Rate limited: 3 submissions per day. Results appear within minutes.</p>
            </div>

            {/* Community submissions */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-purple-500" />
                <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Community Requests</h3>
              </div>
              {communitySubmissions.length > 0 ? (
                <div className="space-y-2" data-testid="community-submissions">
                  {communitySubmissions.map((s) => (
                    <div key={s.submission_id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                      <div>
                        <span className="text-sm font-mono font-medium text-slate-900">{s.domain}</span>
                        <span className="text-[10px] text-slate-400 ml-2">{new Date(s.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {s.sharecard_url && (
                          <Button variant="outline" size="sm" className="h-7 text-[11px] rounded-full gap-1"
                            data-testid={`share-community-${s.domain}`}
                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${s.sharecard_url}`, '_blank')}>
                            <Share2 className="w-3 h-3" /> Share
                          </Button>
                        )}
                        {s.public_id && (
                          <Link to={`/r/${s.public_id}`}>
                            <Button variant="outline" size="sm" className="h-7 text-[11px] rounded-full gap-1">
                              <ExternalLink className="w-3 h-3" /> Report
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 py-4">No community submissions yet. Be the first!</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="border-t border-slate-100 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-2xl">
              <h3 className="text-sm font-semibold text-slate-700 mb-1">Methodology</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                We check {botSetSize} known AI bots (GPTBot, ChatGPT-User, ClaudeBot, Google-Extended, Bytespider, PerplexityBot, and others).
                Rules are detected from each site's <span className="font-mono">robots.txt</span> and public capability files
                (<span className="font-mono">llms.txt</span>, <span className="font-mono">agents.json</span>).
                This reflects <em>declared posture</em>, not enforcement. Sites may enforce differently at runtime.
                Posture is computed deterministically: Restrictive = &ge;75% blocked &amp; 0 allowed; Selective = mix of blocked &amp; allowed; Open = &ge;50% allowed &amp; 0 blocked; Silent = no rules detected.
              </p>
            </div>
            <Link to="/monitor">
              <Button variant="outline" className="h-9 rounded-full text-xs gap-1.5" data-testid="install-monitor-cta">
                <Chrome className="w-3.5 h-3.5" />Install Monitor
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* JSON-LD for SEO — data is server-controlled, JSON.stringify escapes */}
      {mostRestrictive.length > 0 && (() => {
        const jsonLd = JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Most Restrictive Sites for AI Bots",
          "description": `Top sites blocking AI bots (based on ${botSetSize}-bot set, declared in robots.txt)`,
          "numberOfItems": mostRestrictive.length,
          "itemListElement": mostRestrictive.map((e, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": String(e.domain).replace(/</g, '&lt;'),
            "url": `${process.env.REACT_APP_BACKEND_URL}${e.report_url}`,
          })),
        }).replace(/</g, '\\u003c');
        return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />;
      })()}
    </div>
  );
}
